import { useState, useEffect } from 'react'
import { User, Mail, Phone, Building2, Save, Check, ArrowLeft } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { profilesApi } from '../lib/api'
import { useToast } from '../lib/ToastContext'

export default function StaffProfile({ onBack }) {
  const { user, profile, refreshProfile } = useAuth()
  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [loading, setLoading] = useState(false)
  const [saved, setSaved] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '')
      setPhone(profile.phone || '')
    }
  }, [profile])

  const handleSave = async () => {
    if (!user?.id) return
    setLoading(true)
    try {
      await profilesApi.update(user.id, {
        full_name: fullName,
        phone: phone,
      })
      await refreshProfile()
      setSaved(true)
      toast('Profile updated successfully.', 'success')
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      toast('Failed to update profile.', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">My Profile</h1>
          <p className="text-sm text-muted mt-1">Update your personal information</p>
        </div>
        {saved && (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
            <Check size={14} /> Saved
          </span>
        )}
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl p-6">
        <div className="flex items-center gap-4 pb-4 border-b border-neutral-200">
          <div className="w-16 h-16 bg-brand-soft rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-brand">
              {fullName?.charAt(0) || user?.email?.charAt(0) || 'S'}
            </span>
          </div>
          <div>
            <p className="text-sm font-semibold text-ink">{fullName || 'Staff Member'}</p>
            <p className="text-xs text-muted flex items-center gap-1">
              <Mail size={12} /> {user?.email}
            </p>
            <p className="text-xs text-muted flex items-center gap-1">
              <Building2 size={12} /> Staff
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          <div>
            <label className="label-above">Full Name</label>
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="input-flat pl-10"
                placeholder="Your full name"
              />
            </div>
          </div>

          <div>
            <label className="label-above">Phone Number</label>
            <div className="relative">
              <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="input-flat pl-10"
                placeholder="+234 800 000 0000"
              />
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={loading}
            className="btn-primary flex items-center gap-2"
          >
            <Save size={16} />
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}